import { useState, useEffect } from 'react';
import { Product, ProductVariation } from '../../../lib/store/products';
import { useProductVariations } from '../../../hooks/useProducts';
import { useAddToCart } from '../../../hooks/useCart';
import { formatPrice, getPrice, getRegularPrice, getSalePrice, isProductOnSale } from '../../../utils/price';

interface Props {
    product: Product | null;
    onClose: () => void;
    onAddedToCart: (message: string) => void;
}

export default function ProductModal({ product, onClose, onAddedToCart }: Props) {
    const isOpen = !!product;

    const [quantity, setQuantity] = useState(1);
    const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [selectedVariationId, setSelectedVariationId] = useState<number | null>(null);
    const [isAdding, setIsAdding] = useState(false);
    const [addToCartError, setAddToCartError] = useState<string | null>(null);
    const [descriptionHeight, setDescriptionHeight] = useState(80);

    const { data: variations, isLoading: variationsLoading } = useProductVariations(product?.id || 0, {
        enabled: isOpen && !!product && product.type === 'variable',
    });

    const addToCart = useAddToCart();

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setImageErrors(new Set());
            setSelectedImage(null);
            setSelectedVariationId(null);
            setQuantity(1);
            setAddToCartError(null);
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    if (!isOpen || !product) return null;

    // ============================================================
    // OBRAZY
    // ============================================================
    const getAllImages = (): string[] => {
        if (!product.images || product.images.length === 0) return [];

        return product.images
            .map((img) => {
                if (typeof img === 'string') return img;
                if (typeof img === 'object') {
                    return (img as any).src || (img as any).url || null;
                }
                return null;
            })
            .filter((src): src is string => src !== null && src.length > 0);
    };

    const images = getAllImages();
    const mainImage = selectedImage || (images.length > 0 ? images[0] : null);
    const hasError = mainImage ? imageErrors.has(mainImage) : true;

    // ============================================================
    // WARIANTY — oryginalna logika
    // ============================================================
    const hasVariations = product.type === 'variable' && variations && variations.length > 0;

    const getVariationOption = (attr: any): string => {
        if (!attr) return '';
        if (typeof attr === 'string') return attr;
        if (typeof attr === 'object') {
            if (attr.option) {
                if (typeof attr.option === 'string') return attr.option;
                if (typeof attr.option === 'object') {
                    const values = Object.values(attr.option);
                    if (values.length > 0 && typeof values[0] === 'string') {
                        return values[0] as string;
                    }
                }
            }
            if (attr.value) {
                if (typeof attr.value === 'string') return attr.value;
                if (typeof attr.value === 'object') {
                    const values = Object.values(attr.value);
                    if (values.length > 0 && typeof values[0] === 'string') {
                        return values[0] as string;
                    }
                }
            }
        }
        return '';
    };

    // Ustawienie: zmienia kolejność wariantów (true = odwrócona, false = oryginalna)
    const REVERSE_VARIATIONS_ORDER = true;

    const getVariationAttributesArray = (variation: any) => {
        if (!variation.attributes) return [];

        if (Array.isArray(variation.attributes)) {
            let attributes = variation.attributes
                .map((attr: any) => ({
                    name: attr.name || '',
                    slug: attr.slug || attr.name?.toLowerCase().replace(/[^a-z0-9]/g, '_') || '',
                    option: getVariationOption(attr),
                }))
                .filter((a: any) => a.name && a.option);

            if (REVERSE_VARIATIONS_ORDER) {
                attributes = attributes.reverse();
            }
            return attributes;
        }

        if (typeof variation.attributes === 'object') {
            const result: any[] = [];
            for (const [key, value] of Object.entries(variation.attributes)) {
                if (value && typeof value === 'object') {
                    const attrData = value as any;
                    const name = attrData.name || key.replace('attribute_', '');
                    const slug =
                        attrData.slug ||
                        attrData.name?.toLowerCase().replace(/[^a-z0-9]/g, '_') ||
                        name.toLowerCase().replace(/[^a-z0-9]/g, '_');
                    const option = getVariationOption(value);
                    if (name && option) {
                        result.push({ name, slug, option });
                    }
                }
            }
            if (REVERSE_VARIATIONS_ORDER) {
                return result.reverse();
            }
            return result;
        }

        return [];
    };

    const getSelectedVariation = () => {
        if (!variations || variations.length === 0) return null;
        return variations.find((v: ProductVariation) => v.id === selectedVariationId) || null;
    };

    const selectedVariation = getSelectedVariation();

    const getVariationLabel = (variation: any) => {
        const attrs = getVariationAttributesArray(variation);
        if (attrs.length > 0) {
            return attrs.map((a: any) => a.option).join(' / ');
        }
        return variation.name || `Wariant ${variation.id}`;
    };

    // ============================================================
    // CENY
    // ============================================================
    const basePrice = getPrice(product);
    const baseRegularPrice = getRegularPrice(product);
    const baseSalePrice = getSalePrice(product);

    const currentPrice = selectedVariation ? getPrice(selectedVariation) : basePrice;
    const currentRegularPrice = selectedVariation ? getRegularPrice(selectedVariation) : baseRegularPrice;
    const currentSalePrice = selectedVariation ? getSalePrice(selectedVariation) : baseSalePrice;

    const onSale = isProductOnSale(product) || (selectedVariation ? isProductOnSale(selectedVariation) : false);

    // ============================================================
    // DOSTĘPNOŚĆ
    // ============================================================
    const isInStock = () => {
        if (hasVariations && selectedVariation) {
            const status = selectedVariation.stock_status || 'instock';
            return !status || status === 'instock' || status === 'onbackorder';
        }
        const status = product.stock_status || 'instock';
        return !status || status === 'instock' || status === 'onbackorder';
    };

    const inStock = isInStock();

    // Dla produktu wariantowego przycisk musi być zablokowany, dopóki lista
    // wariantów się nie załaduje — inaczej można kliknąć "Dodaj do koszyka"
    // zanim aplikacja w ogóle wie, jakie warianty istnieją.
    const isLoadingVariations = product.type === 'variable' && variationsLoading;

    // ============================================================
    // DODAWANIE DO KOSZYKA
    // ============================================================
    const handleAddToCart = async () => {
        if (isAdding || isLoadingVariations) return;
        setAddToCartError(null);

        if (hasVariations && !selectedVariationId) {
            setAddToCartError('Wybierz wariant przed dodaniem do koszyka');
            return;
        }

        if (!inStock) {
            setAddToCartError('Produkt jest niedostępny');
            return;
        }

        setIsAdding(true);

        try {
            const params: any = {
                id: String(product.id),
                quantity: String(quantity),
            };

            if (selectedVariation && selectedVariation.id) {
                params.variation_id = String(selectedVariation.id);

                const attrs = getVariationAttributesArray(selectedVariation);
                if (attrs.length > 0) {
                    const variation: Record<string, string> = {};
                    attrs.forEach((attr: any) => {
                        const attrName = attr.name.toLowerCase();
                        variation[attrName] = String(attr.option);
                    });
                    params.variation = variation;
                }
            }

            await addToCart.mutateAsync(params);

            onAddedToCart(`Dodano do koszyka: ${product.name} (x${quantity})`);

            // Produkt z wariantami: modal zostaje otwarty, żeby ułatwić
            // dodanie kolejnego wariantu bez ponownego otwierania modala.
            // Produkt prosty: modal się zamyka, tak jak dotychczas.
            if (!hasVariations) {
                onClose();
            } else {
                setQuantity(1);
            }
        } catch (error: any) {
            console.error('❌ Błąd dodawania do koszyka:', error);
            setAddToCartError(error?.response?.data?.message || 'Nie udało się dodać produktu do koszyka');
        } finally {
            setIsAdding(false);
        }
    };

    const handleImageError = (src: string) => {
        setImageErrors((prev) => new Set(prev).add(src));
    };

    // ============================================================
    // RENDER
    // ============================================================
    return (
        <div
            className="modal-overlay"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className="modal-content">
                <button className="modal-close" onClick={onClose}>
                    ✕
                </button>

                <div className="modal-grid">
                    <div className="modal-gallery">
                        <div className="modal-main-image">
                            {mainImage && !hasError ? (
                                <img src={mainImage} alt={product.name} onError={() => handleImageError(mainImage)} />
                            ) : (
                                <span className="modal-placeholder">📷</span>
                            )}
                        </div>

                        {images.length > 1 && (
                            <div className="modal-thumbnails">
                                {images.map((src, index) => {
                                    const isError = imageErrors.has(src);
                                    return (
                                        <button
                                            key={index}
                                            className={`modal-thumbnail ${selectedImage === src ? 'active' : ''}`}
                                            onClick={() => setSelectedImage(src)}
                                        >
                                            {isError ? (
                                                <span>📷</span>
                                            ) : (
                                                <img
                                                    src={src}
                                                    alt={`Miniatura ${index + 1}`}
                                                    onError={() => handleImageError(src)}
                                                />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="modal-info">
                        <div className="modal-info-scroll">
                            <h2 className="modal-title">{product.name}</h2>

                <div className="modal-price">
                  <h4 className="modal-variations-title">Cena:</h4>
                                {onSale && currentSalePrice && currentSalePrice !== '0' ? (
                                    <>
                                        <span className="modal-price-sale">{formatPrice(currentSalePrice)}</span>
                                        <span className="modal-price-regular">
                                            {formatPrice(currentRegularPrice || currentPrice)}
                                        </span>
                                    </>
                                ) : (
                                    <span className="modal-price-current">{formatPrice(currentPrice)}</span>
                                )}
                            </div>

                            {hasVariations && (
                                <div className="modal-variations">
                                    <h4 className="modal-variations-title">Dostępne opcje:</h4>
                                    <div className="modal-variations-list">
                                      {(() => {
                                          const sortedVariations = REVERSE_VARIATIONS_ORDER
                                              ? [...variations!].reverse()
                                              : variations!;

                                          return sortedVariations.map((variation: ProductVariation) => {
                                              const label = getVariationLabel(variation);
                                              const isSelected = selectedVariationId === variation.id;
                                              return (
                                                  <button
                                                      key={variation.id}
                                                      className={`modal-variant-btn ${isSelected ? 'active' : ''}`}
                                                      onClick={() => setSelectedVariationId(variation.id)}
                                                  >
                                                      {label}
                                                  </button>
                                              );
                                          });
                                      })()}
                                    </div>
                                    {variationsLoading && <div className="modal-loading">Ładowanie wariantów...</div>}
                                </div>
                            )}

                            <div className="modal-description">
                                {(() => {
                                    const descriptionHtml = product.description || product.short_description || '';
                                    if (!descriptionHtml) return null;

                                    return (
                                        <iframe
                                            title="Opis produktu"
                                            scrolling="no"
                                            style={{
                                                width: '100%',
                                                border: 'none',
                                                display: 'block',
                                                height: descriptionHeight,
                                            }}
                                            srcDoc={`<!DOCTYPE html><html><head><base target="_parent"><style>
                                                body { margin: 0; font-family: Arial, Helvetica, sans-serif; line-height: 1.8; color: #333; }
                                                p { margin: 0 0 10px; }
                                                ul, ol { margin: 0 0 10px; padding-left: 20px; }
                                                li { margin-bottom: 4px; }
                                                img { max-width: 100%; height: auto; }
                                            </style></head><body>${descriptionHtml}</body></html>`}
                                            onLoad={(e) => {
                                                const doc = e.currentTarget.contentDocument;
                                                if (doc?.body) {
                                                    setDescriptionHeight(doc.body.scrollHeight + 10);
                                                }
                                            }}
                                        />
                                    );
                                })()}
                            </div>

                            <div className="modal-quantity">
                                <label>Ilość</label>
                                <div className="modal-quantity-controls">
                                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))}>-</button>
                                    <span>{quantity}</span>
                                    <button onClick={() => setQuantity(quantity + 1)}>+</button>
                                </div>
                            </div>

                            {addToCartError && <div className="modal-error">{addToCartError}</div>}
                        </div>

                        <button
                            className={`modal-add-btn ${
                                isAdding || !inStock || isLoadingVariations || (hasVariations && !selectedVariationId)
                                    ? 'disabled'
                                    : ''
                            }`}
                            onClick={handleAddToCart}
                            disabled={
                                isAdding || !inStock || isLoadingVariations || (hasVariations && !selectedVariationId)
                            }
                        >
                            {isAdding
                                ? 'Dodawanie...'
                                : isLoadingVariations
                                  ? 'Ładowanie wariantów...'
                                  : !inStock
                                    ? 'Produkt niedostępny'
                                    : hasVariations && !selectedVariationId
                                      ? 'Wybierz wariant'
                                      : 'Dodaj do koszyka'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
